import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { createBrowserRouter, Navigate, Outlet, useNavigate, useRouteError } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RequireAuth, RequireOnboardingComplete, RequireRole } from '@features/auth/RouteGuards';
import { useAuth } from '@features/auth/AuthContext';
import { computeAuthenticatedLanding } from '@features/auth/landingTarget';
import { InstallPrompt } from '@app/components/InstallPrompt';

// -- Existing screens (kept exactly as the Figma export delivered them) -------
const SplashScreen = lazy(() =>
  import('@app/components/screens/SplashScreen').then((m) => ({ default: m.SplashScreen })),
);
const LanguageSelector = lazy(() =>
  import('@app/components/screens/LanguageSelector').then((m) => ({
    default: m.LanguageSelector,
  })),
);
const OnboardingScreen = lazy(() =>
  import('@app/components/screens/OnboardingScreen').then((m) => ({
    default: m.OnboardingScreen,
  })),
);
const LocationPermissionScreen = lazy(() =>
  import('@app/components/screens/LocationPermissionScreen').then((m) => ({
    default: m.LocationPermissionScreen,
  })),
);
const LocationConfirmScreen = lazy(() =>
  import('@app/components/screens/LocationConfirmScreen').then((m) => ({
    default: m.LocationConfirmScreen,
  })),
);
// Settings sub-routes were figma-export shells with hardcoded mock data
// (Codex iter-3 review item #1). Iter-4 replaces every authenticated
// settings sub-route with a production route under
// src/features/settings/routes/* that hits the real preferences API.
const ProfileSettingsRoute = lazy(() =>
  import('@features/settings/routes/ProfileSettingsRoute').then((m) => ({
    default: m.ProfileSettingsRoute,
  })),
);
const NotificationsSettingsRoute = lazy(() =>
  import('@features/settings/routes/NotificationsSettingsRoute').then((m) => ({
    default: m.NotificationsSettingsRoute,
  })),
);
const LanguageSettingsRoute = lazy(() =>
  import('@features/settings/routes/LanguageSettingsRoute').then((m) => ({
    default: m.LanguageSettingsRoute,
  })),
);
const ThemeSettingsRoute = lazy(() =>
  import('@features/settings/routes/ThemeSettingsRoute').then((m) => ({
    default: m.ThemeSettingsRoute,
  })),
);
const PrivacySettingsRoute = lazy(() =>
  import('@features/settings/routes/PrivacySettingsRoute').then((m) => ({
    default: m.PrivacySettingsRoute,
  })),
);

// -- Production feature routes (Gates 4 + iter-2) ----------------------------
const LoginRoute = lazy(() =>
  import('@features/auth/routes/LoginRoute').then((m) => ({ default: m.LoginRoute })),
);
const HomeRoute = lazy(() =>
  import('@features/home/routes/HomeRoute').then((m) => ({ default: m.HomeRoute })),
);
const MapRoute = lazy(() =>
  import('@features/navigation/routes/MapRoute').then((m) => ({ default: m.MapRoute })),
);
const HelpRoute = lazy(() =>
  import('@features/help/routes/HelpRoute').then((m) => ({ default: m.HelpRoute })),
);
const SettingsRoute = lazy(() =>
  import('@features/settings/routes/SettingsRoute').then((m) => ({ default: m.SettingsRoute })),
);
const HealthWizardRoute = lazy(() =>
  import('@features/health-card/routes/HealthWizardRoute').then((m) => ({
    default: m.HealthWizardRoute,
  })),
);
const SosRoute = lazy(() =>
  import('@features/sos/routes/SosRoute').then((m) => ({ default: m.SosRoute })),
);
const AssistanceRoute = lazy(() =>
  import('@features/assistance/routes/AssistanceRoute').then((m) => ({
    default: m.AssistanceRoute,
  })),
);
const HealthCardRoute = lazy(() =>
  import('@features/health-card/routes/HealthCardRoute').then((m) => ({
    default: m.HealthCardRoute,
  })),
);
const HealthCardViewRoute = lazy(() =>
  import('@features/health-card/routes/HealthCardViewRoute').then((m) => ({
    default: m.HealthCardViewRoute,
  })),
);
const HealthUnitsRoute = lazy(() =>
  import('@features/places/routes/HealthUnitsRoute').then((m) => ({ default: m.HealthUnitsRoute })),
);
const RestPointsRoute = lazy(() =>
  import('@features/places/routes/RestPointsRoute').then((m) => ({ default: m.RestPointsRoute })),
);
const PlaceDetailRoute = lazy(() =>
  import('@features/places/routes/PlaceDetailRoute').then((m) => ({ default: m.PlaceDetailRoute })),
);
const NotificationsRoute = lazy(() =>
  import('@features/notifications/routes/NotificationsRoute').then((m) => ({
    default: m.NotificationsRoute,
  })),
);
const GuidanceRoute = lazy(() =>
  import('@features/guidance/routes/GuidanceRoute').then((m) => ({ default: m.GuidanceRoute })),
);
const GuidanceTopicDetailRoute = lazy(() =>
  import('@features/guidance/routes/GuidanceTopicDetailRoute').then((m) => ({ default: m.GuidanceTopicDetailRoute })),
);
const DuasRoute = lazy(() =>
  import('@features/duas/routes/DuasRoute').then((m) => ({ default: m.DuasRoute })),
);
const DuaDetailRoute = lazy(() =>
  import('@features/duas/routes/DuaDetailRoute').then((m) => ({ default: m.DuaDetailRoute })),
);
const ProfileCompleteRoute = lazy(() =>
  import('@features/users/routes/ProfileCompleteRoute').then((m) => ({
    default: m.ProfileCompleteRoute,
  })),
);
const ProfileTypeRoute = lazy(() =>
  import('@features/users/routes/ProfileTypeRoute').then((m) => ({
    default: m.ProfileTypeRoute,
  })),
);
const LocationSettingsRoute = lazy(() =>
  import('@features/settings/routes/LocationSettingsRoute').then((m) => ({
    default: m.LocationSettingsRoute,
  })),
);
const HelpSettingsRoute = lazy(() =>
  import('@features/settings/routes/HelpSettingsRoute').then((m) => ({
    default: m.HelpSettingsRoute,
  })),
);
const VolunteerHomeRoute = lazy(() =>
  import('@features/volunteers/routes/VolunteerHomeRoute').then((m) => ({
    default: m.VolunteerHomeRoute,
  })),
);
const VolunteerStatusRoute = lazy(() =>
  import('@features/volunteers/routes/VolunteerStatusRoute').then((m) => ({
    default: m.VolunteerStatusRoute,
  })),
);
const VolunteerRequestsRoute = lazy(() =>
  import('@features/volunteers/routes/VolunteerRequestsRoute').then((m) => ({
    default: m.VolunteerRequestsRoute,
  })),
);
const VolunteerAssignmentRoute = lazy(() =>
  import('@features/volunteers/routes/VolunteerAssignmentRoute').then((m) => ({
    default: m.VolunteerAssignmentRoute,
  })),
);
const VolunteerHistoryRoute = lazy(() =>
  import('@features/volunteers/routes/VolunteerHistoryRoute').then((m) => ({
    default: m.VolunteerHistoryRoute,
  })),
);
const VolunteerProfileRoute = lazy(() =>
  import('@features/volunteers/routes/VolunteerProfileRoute').then((m) => ({
    default: m.VolunteerProfileRoute,
  })),
);
const VolunteerNotificationsRoute = lazy(() =>
  import('@features/volunteers/routes/VolunteerNotificationsRoute').then((m) => ({
    default: m.VolunteerNotificationsRoute,
  })),
);
const VolunteerWaitingApprovalRoute = lazy(() =>
  import('@features/volunteers/routes/VolunteerWaitingApprovalRoute').then((m) => ({
    default: m.VolunteerWaitingApprovalRoute,
  })),
);
const VolunteerVerificationRoute = lazy(() =>
  import('@features/volunteers/routes/VolunteerVerificationRoute').then((m) => ({
    default: m.VolunteerVerificationRoute,
  })),
);
const VolunteerPlaceSubmitRoute = lazy(() =>
  import('@features/volunteers/routes/VolunteerPlaceSubmitRoute').then((m) => ({
    default: m.VolunteerPlaceSubmitRoute,
  })),
);
const VolunteerPlaceSubmissionsRoute = lazy(() =>
  import('@features/volunteers/routes/VolunteerPlaceSubmissionsRoute').then((m) => ({
    default: m.VolunteerPlaceSubmissionsRoute,
  })),
);

// -- Layout pieces ------------------------------------------------------------
function FullPageFallback(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div role="status" aria-live="polite" className="flex h-screen items-center justify-center">
      <span>{t('common.loading')}</span>
    </div>
  );
}

function LazyOutlet(): JSX.Element {
  return (
    <Suspense fallback={<FullPageFallback />}>
      <Outlet />
    </Suspense>
  );
}

// iter-46: RootShell sits at the root route so it can host any global UI
// that needs the router context (InstallPrompt's useLocation, etc.).
// Previously InstallPrompt was rendered as a sibling of RouterProvider in
// App.tsx, which means useLocation() was called OUTSIDE the router and
// threw an invariant — stripped of its message by the production build,
// it surfaced as a generic "Error: (no message)" through the root error
// boundary.
function RootShell(): JSX.Element {
  return (
    <>
      <LazyOutlet />
      <InstallPrompt />
    </>
  );
}

function NotFound(): JSX.Element {
  return <Navigate to="/app/home" replace />;
}

// iter-63: route-level error boundary. iter-62 added a window.error
// recovery hook for the runtime DOM-teardown crash (SDK vs React),
// but in practice React Router catches the error itself and renders
// its default "Unexpected Application Error!" page BEFORE the error
// reaches window — so the iter-62 handler never fires for this
// specific crash. This component runs INSIDE the router as the
// errorElement, sees the same error directly via useRouteError(),
// and:
//   1. If it matches the SDK-teardown signature, immediately
//      unregisters the SW, clears caches, and reloads. Users on a
//      cached buggy bundle self-heal without ever seeing a broken
//      screen for more than a moment.
//   2. Otherwise renders a friendly Arabic error card with a
//      "back to home" + "reload" choice instead of the raw default.
const RECOVERY_KEY = 'yusr.router-error-reloaded';

function isStaleTeardownError(message: string | null | undefined): boolean {
  if (!message) return false;
  // The original DOM-teardown crash signature (SDK vs React).
  if (
    (message.includes('removeChild') &&
      (message.includes('not a child') ||
       message.includes('object can not be found') ||
       message.includes('NotFoundError'))) ||
    message.includes('The object can not be found here')
  ) return true;

  // iter-68: stale-chunk signatures that surface INSIDE React Router
  // (so iter-49's window.onerror handler never sees them). When a new
  // deploy ships, the cached index.html still references the previous
  // chunk hashes; the fetch for a chunk URL falls through to the SPA
  // HTML fallback ("Failed to load module script… MIME type of
  // 'text/html'"); the dynamic import rejects; React Router's
  // ErrorBoundary catches a TypeError of the form "Cannot read
  // properties of undefined (reading 'HomeRoute')" because the
  // lazy()-resolved module is undefined.
  if (
    message.includes('Failed to load module script') ||
    message.includes('MIME type of "text/html"') ||
    message.includes("MIME type of 'text/html'") ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    /Loading (chunk|CSS chunk) [^\s]+ failed/.test(message) ||
    // Typical lazy-import-failed manifestation: the chunk URL returned
    // HTML, the module import resolves to `undefined`, and we crash
    // accessing the named export. The export name pattern matches
    // *Route / *Screen / *Page, which covers every lazy() in router.tsx.
    /Cannot read properties of undefined \(reading '[A-Z][A-Za-z0-9_]*(Route|Screen|Page)'\)/.test(message) ||
    // Older Safari / Firefox phrasings of the same crash.
    /undefined is not an object \(evaluating '[^']*\.[A-Z][A-Za-z0-9_]*(Route|Screen|Page)'\)/.test(message)
  ) return true;

  return false;
}

async function hardRecover(): Promise<void> {
  try {
    if (sessionStorage.getItem(RECOVERY_KEY) === '1') return;
    sessionStorage.setItem(RECOVERY_KEY, '1');
  } catch { /* private mode — keep going */ }
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch { /* partial recovery is fine */ }
  window.location.reload();
}

function RouterErrorBoundary(): JSX.Element {
  const error = useRouteError();
  const message = (() => {
    if (!error) return null;
    if (typeof error === 'string') return error;
    if (typeof error === 'object' && 'message' in error) {
      return String((error as { message: unknown }).message ?? '');
    }
    return String(error);
  })();
  const isRecoverable = isStaleTeardownError(message);

  useEffect(() => {
    if (isRecoverable) {
      void hardRecover();
    }
    // Always log so we can audit live captures even if recovery handles it.
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn('[RouterErrorBoundary]', { message, error });
    }
  }, [isRecoverable, message, error]);

  if (isRecoverable) {
    return (
      <div
        dir="rtl"
        className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gradient-to-b from-[#F5EFE7] to-white px-6 text-center text-[#171717]"
        role="status"
        aria-live="polite"
      >
        <span
          aria-hidden
          className="block h-10 w-10 animate-spin rounded-full border-[3px] border-[#9C7848]/25 border-t-[#9C7848]"
        />
        <p className="text-base font-bold">جاري تحديث التطبيق…</p>
        <p className="text-sm text-neutral-600">ثواني معدودة وهترجع تكمل اللي كنت بتعمله.</p>
      </div>
    );
  }

  return (
    <div
      dir="rtl"
      className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gradient-to-b from-[#F5EFE7] to-white px-6 text-center text-[#171717]"
    >
      <p className="text-lg font-bold">حدث خطأ غير متوقع</p>
      <p className="text-sm text-neutral-600">حاول إعادة تحميل الصفحة. لو استمرت المشكلة ابعت الخطأ لفريق الدعم.</p>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => window.location.assign('/app/home')}
          className="rounded-2xl bg-[#9C7848] px-4 py-2.5 text-sm font-bold text-white"
        >
          الصفحة الرئيسية
        </button>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-2xl border border-[#9C7848] bg-white px-4 py-2.5 text-sm font-bold text-[#9C7848]"
        >
          إعادة المحاولة
        </button>
      </div>
    </div>
  );
}

// iter-53: SplashRoute is now auth-aware. A returning user with a valid
// session (token in localStorage, /auth/me hydrates successfully) skips
// the language/onboarding/location chain entirely and lands on the right
// shell immediately. Without this, the 30-day Sanctum token was wasted —
// every fresh app open forced the user back through OTP login.
function SplashRoute(): JSX.Element {
  const { user, status } = useAuth();
  const navigate = useNavigate();
  const [splashDone, setSplashDone] = useState(false);
  const navigatedRef = useRef(false);

  useEffect(() => {
    if (navigatedRef.current) return;
    // Wait for AuthContext to finish hydrating from /auth/me before
    // deciding. status is 'loading' only while a stored token is being
    // exchanged for the user record.
    if (status === 'loading') return;

    if (status === 'authenticated' && user) {
      navigatedRef.current = true;
      navigate(computeAuthenticatedLanding(user), { replace: true });
      return;
    }

    // Unauthenticated: preserve the existing brand splash → language →
    // onboarding → ... → login chain. Only jump after the splash finishes
    // its 3-second animation. Kept on window.location.assign to match the
    // hard-navigation pattern the rest of the onboarding chain uses
    // (LanguageSelector / OnboardingScreen / location screens).
    if (splashDone) {
      navigatedRef.current = true;
      window.location.assign('/language');
    }
  }, [status, user, splashDone, navigate]);

  return <SplashScreen onComplete={() => setSplashDone(true)} />;
}

// (The figma-export `onBack` / `onNavigate` adapters used in iter-1 are gone —
// every authenticated route is now a production *Route under src/features/*
// that uses react-router hooks directly. Codex iter-3 review item #1.)

export const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <RootShell />,
      // iter-63: catch route-level errors (including the SDK-vs-React
      // teardown crash) here, run auto-recovery, and surface a friendly
      // Arabic UI instead of React Router's default English page.
      errorElement: <RouterErrorBoundary />,
      children: [
        { index: true, element: <SplashRoute /> },
        { path: 'splash', element: <SplashRoute /> },
        {
          path: 'language',
          element: <LanguageSelector onSelect={() => window.location.assign('/onboarding')} />,
        },
        {
          path: 'onboarding',
          element: (
            <OnboardingScreen onComplete={() => window.location.assign('/location/permission')} />
          ),
        },
        {
          path: 'location/permission',
          element: (
            <LocationPermissionScreen
              onContinue={() => window.location.assign('/location/confirm')}
            />
          ),
        },
        {
          path: 'location/confirm',
          element: (
            <LocationConfirmScreen
              onConfirm={() => window.location.assign('/auth/login')}
              onAdjust={() => window.location.assign('/location/permission')}
            />
          ),
        },
        { path: 'auth/login', element: <LoginRoute /> },
        // iter-30.1: onboarding routes call authed endpoints (PATCH
        // /users/me, PUT /health-card). If the session expires mid-flow
        // the API client clears the token and flips AuthContext state
        // to "unauthenticated", but until these routes are wrapped in
        // RequireAuth there's nothing to redirect the user off the
        // screen — they end up looking at a stale form with an
        // "Unauthenticated." error inline. Wrapping fixes both the
        // first-load case (user with no token can't open onboarding
        // directly) and the in-flight expiry case.
        {
          path: 'profile/complete',
          element: <RequireAuth><ProfileCompleteRoute /></RequireAuth>,
        },
        {
          path: 'profile/type',
          element: <RequireAuth><ProfileTypeRoute /></RequireAuth>,
        },
        {
          path: 'health/onboarding',
          element: <RequireAuth><HealthWizardRoute /></RequireAuth>,
        },

        // -- Pilgrim app shell --
        // iter-38: gate /app/* behind a hajj/umrah role check so a
        // volunteer who somehow lands here (e.g. via the back arrow on
        // /volunteer that historically pointed at /app/home) is bounced
        // to /volunteer instead of seeing the pilgrim Settings list
        // (which links to /app/health-card — volunteers have no health
        // card, so the form is meaningless for them).
        {
          path: 'app',
          element: (
            <RequireAuth>
              <RequireOnboardingComplete>
                <RequireRole roles={['hajj', 'umrah']} fallback="/volunteer">
                  <LazyOutlet />
                </RequireRole>
              </RequireOnboardingComplete>
            </RequireAuth>
          ),
          children: [
            { index: true, element: <Navigate to="/app/home" replace /> },
            { path: 'home', element: <HomeRoute /> },
            { path: 'map', element: <MapRoute /> },
            { path: 'help', element: <HelpRoute /> },
            { path: 'sos', element: <SosRoute /> },
            { path: 'sos/emergency', element: <SosRoute /> },
            { path: 'sos/:id', element: <SosRoute /> },
            { path: 'health-card', element: <HealthCardRoute /> },
            { path: 'health-card/view', element: <HealthCardViewRoute /> },
            { path: 'places/health-units', element: <HealthUnitsRoute /> },
            { path: 'places/health-units/:id', element: <PlaceDetailRoute kind="health-units" /> },
            { path: 'places/rest-points', element: <RestPointsRoute /> },
            { path: 'places/rest-points/:id', element: <PlaceDetailRoute kind="rest-points" /> },
            { path: 'assistance', element: <AssistanceRoute /> },
            { path: 'assistance/new', element: <Navigate to="/app/assistance" replace /> },
            { path: 'assistance/active', element: <AssistanceRoute /> },
            { path: 'assistance/feedback', element: <AssistanceRoute /> },
            { path: 'assistance/chat', element: <AssistanceRoute /> },
            { path: 'notifications', element: <NotificationsRoute /> },
            { path: 'guidance', element: <GuidanceRoute /> },
            { path: 'guidance/:id', element: <GuidanceTopicDetailRoute /> },
            { path: 'duas', element: <DuasRoute /> },
            { path: 'duas/:id', element: <DuaDetailRoute /> },
            { path: 'settings', element: <SettingsRoute /> },
            { path: 'settings/profile', element: <ProfileSettingsRoute /> },
            { path: 'settings/notifications', element: <NotificationsSettingsRoute /> },
            { path: 'settings/language', element: <LanguageSettingsRoute /> },
            { path: 'settings/theme', element: <ThemeSettingsRoute /> },
            { path: 'settings/privacy', element: <PrivacySettingsRoute /> },
            { path: 'settings/location', element: <LocationSettingsRoute /> },
            { path: 'settings/help', element: <HelpSettingsRoute /> },
          ],
        },

        // -- Volunteer PWA shell --
        {
          path: 'volunteer',
          element: (
            <RequireAuth>
              <RequireRole roles={['volunteer']}>
                <LazyOutlet />
              </RequireRole>
            </RequireAuth>
          ),
          children: [
            { index: true, element: <VolunteerHomeRoute /> },
            { path: 'status', element: <VolunteerStatusRoute /> },
            { path: 'requests', element: <VolunteerRequestsRoute /> },
            { path: 'assignments/:id', element: <VolunteerAssignmentRoute /> },
            { path: 'history',       element: <VolunteerHistoryRoute /> },
            { path: 'profile',       element: <VolunteerProfileRoute /> },
            { path: 'notifications', element: <VolunteerNotificationsRoute /> },
            { path: 'pending',       element: <VolunteerWaitingApprovalRoute /> },
            { path: 'verification',  element: <VolunteerVerificationRoute /> },
            // iter-18b: volunteer place contribution flow
            { path: 'places/submit',       element: <VolunteerPlaceSubmitRoute /> },
            { path: 'places/submissions',  element: <VolunteerPlaceSubmissionsRoute /> },
          ],
        },

        { path: '*', element: <NotFound /> },
      ],
    },
  ],
  {
    future: {
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_relativeSplatPath: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);
